// Contact form used across the marketing pages. Posts to /api/contact, which
// delivers the message to team@getsquarefare.com.
import { useMemo, useState } from 'react';
import { COLORS, FONTS, HEADING, TYPE } from 'styles/theme';
import { BUTTON, PAD, SURFACE, WRAP } from 'styles/blocks';
import { submitContactForm } from 'services/publicFlowService';

// Underline fields, transparent ground: the form takes the colour of whatever
// band it sits in rather than punching white boxes into it.
const FIELD = {
    // 16px: anything smaller makes iPhones zoom the page on focus (SFLANDING-142).
    width: '100%', padding: '12px 2px', fontSize: '16px', fontFamily: FONTS.sans,
    border: 'none', borderRadius: 0, borderBottom: `1px solid ${SURFACE.border}`,
    background: 'transparent', color: COLORS.dark, boxSizing: 'border-box',
    outline: 'none', transition: 'border-color 0.15s',
};

// Inline styles can't express :focus, so the highlight is component state.
const FIELD_FOCUSED = { ...FIELD, borderBottom: `2px solid ${COLORS.orange}`, paddingBottom: '11px' };

// `half` fields pair up two-to-a-row when they sit next to each other.
const FIELD_DEFS = {
    name: { label: 'Name', required: true, autoComplete: 'name', half: true },
    firstName: { label: 'First Name', required: true, autoComplete: 'given-name', half: true },
    lastName: { label: 'Last Name', required: true, autoComplete: 'family-name', half: true },
    email: { label: 'Email', type: 'email', required: true, autoComplete: 'email', half: true },
    phone: { label: 'Phone (optional)', type: 'tel', autoComplete: 'tel' },
    workplace: { label: 'Where you work', required: true },
    message: { label: 'Message', required: true, multiline: true },
    newsletter: { label: 'Sign up for news and updates', checkbox: true },
};

const DEFAULT_FIELDS = ['name', 'email', 'phone', 'message'];

// A field is a FIELD_DEFS key, or an object that overrides one / declares a
// one-off field, so page-specific labels stay out of this shared component.
const resolveField = (entry) => {
    if (typeof entry === 'string') return FIELD_DEFS[entry] && { key: entry, ...FIELD_DEFS[entry] };
    return entry?.key && { ...FIELD_DEFS[entry.key], ...entry };
};

/** Group the requested fields into rows, pairing adjacent half-width ones. */
function toRows(defs) {
    const rows = [];
    let pending = null;
    defs.forEach((def) => {
        if (!def.half) {
            if (pending) { rows.push([pending]); pending = null; }
            rows.push([def]);
        } else if (pending) {
            rows.push([pending, def]);
            pending = null;
        } else {
            pending = def;
        }
    });
    if (pending) rows.push([pending]);
    return rows;
}

export default function ContactFormBlock({
    formType = 'general', heading, intro, fields = DEFAULT_FIELDS, submitLabel = 'Send Message',
    inline = false,
}) {
    const defs = useMemo(() => fields.map(resolveField).filter(Boolean), [fields]);
    const [form, setForm] = useState(() =>
        Object.fromEntries(defs.map((def) => [def.key, def.checkbox ? false : ''])));
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');
    const [focused, setFocused] = useState(null);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const toggle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (sending) return;
        setSending(true);
        setError('');
        // Split first/last name fields still post a single `name`, which is what
        // the backend puts in the email subject.
        const name = form.name || [form.firstName, form.lastName].filter(Boolean).join(' ');
        const res = await submitContactForm({ ...form, name, formType });
        setSending(false);
        if (res.success) setSent(true);
        else setError(res.error || 'Could not send your message. Please try again.');
    };

    const renderField = (def) => {
        const { key } = def;
        const field = focused === key ? FIELD_FOCUSED : FIELD;
        const focus = { onFocus: () => setFocused(key), onBlur: () => setFocused(null) };

        if (def.checkbox) {
            return (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <input
                        type="checkbox"
                        checked={form[key]}
                        onChange={toggle(key)}
                        style={{ width: '17px', height: '17px', accentColor: COLORS.dark, cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', color: COLORS.dark }}>{def.label}</span>
                </label>
            );
        }

        return (
            <label key={key} style={{ display: 'block' }}>
                <span style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: COLORS.dark }}>
                    {def.label}{def.required ? ' *' : ''}
                </span>
                {def.multiline ? (
                    <textarea
                        style={{ ...field, minHeight: '140px', resize: 'vertical' }}
                        value={form[key]}
                        onChange={set(key)}
                        required={def.required}
                        {...focus}
                    />
                ) : def.select ? (
                    // The empty option stays selectable so an optional dropdown can
                    // be left unanswered; `required` catches the rest.
                    <select style={field} value={form[key]} onChange={set(key)} required={def.required} {...focus}>
                        <option value="">Select an option</option>
                        {(def.options || []).map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                ) : (
                    <input
                        style={field}
                        type={def.type || 'text'}
                        value={form[key]}
                        onChange={set(key)}
                        required={def.required}
                        autoComplete={def.autoComplete}
                        {...focus}
                    />
                )}
            </label>
        );
    };

    return (
        // `inline` drops the section chrome so the form can sit in a column that
        // already provides its own width and padding. Standalone, it takes the
        // same 860px reading measure as the `text` blocks above it, so the
        // fields line up with the copy instead of floating mid-page.
        <section style={inline ? { width: '100%' } : { ...WRAP, padding: `40px ${PAD}px` }}>
            {heading && (
                <h2 style={{ ...HEADING, fontSize: TYPE.bandHeading, margin: '0 0 12px', color: COLORS.dark }}>
                    {heading}
                </h2>
            )}
            {intro && (
                <p style={{ fontSize: TYPE.body, lineHeight: 1.7, color: COLORS.text, margin: '0 0 24px' }}>{intro}</p>
            )}

            {sent ? (
                <div style={{
                    background: '#f4f8f4', border: '1px solid #cfe3cf', color: '#2c5f2c',
                    borderRadius: '12px', padding: '24px', fontSize: '15px', lineHeight: 1.6,
                }}>
                    Thank you! Your message has been sent — we'll get back to you shortly.
                </div>
            ) : (
                <form onSubmit={onSubmit} style={{ display: 'grid', gap: '14px' }}>
                    {toRows(defs).map((row, i) => (
                        row.length > 1 ? (
                            <div key={i} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' }}>
                                {row.map(renderField)}
                            </div>
                        ) : renderField(row[0])
                    ))}

                    {error && <p style={{ color: '#cc3333', fontSize: '14px', margin: 0 }} role="alert">{error}</p>}

                    <button type="submit" disabled={sending} style={{
                        ...BUTTON, justifySelf: 'start', border: 'none',
                        cursor: sending ? 'default' : 'pointer', opacity: sending ? 0.6 : 1,
                    }}>
                        {sending ? 'Sending…' : submitLabel}
                    </button>
                </form>
            )}
        </section>
    );
}
